import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { authService } from '../services/supabase/auth.service';
import { agencyMarkerService } from '../services/supabase/agency-marker.service';
import { User, UserUpdateInput } from '../types/user';
import { ChangelogService } from '../services/supabase/changelog.service';
import logger from '../utils/logger';

// Interface pour l'Ã©tat d'authentification
interface AuthState {
  supabaseUser: SupabaseUser | null;
  userProfile: User | null;
  agencyName: string | null;
  agencyAddress: string | null;
  session: Session | null;
  loading: boolean;
  error: Error | null;
  shouldShowChangelog: boolean;
}

// Interface pour les donnÃ©es d'inscription
interface SignUpData {
  email: string;
  password: string;
  name: string;
  firstName: string;
  agencyName: string;
  agencyAddress: string;
  photoFile: File | null;
}

// Interface pour le contexte d'authentification
interface AuthContextType extends AuthState {
  signUp: (data: SignUpData) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateUserProfile: (data: UserUpdateInput) => Promise<void>;
  clearError: () => void;
  checkChangelogStatus: () => Promise<void>;
  markChangelogAsSeen: () => Promise<void>;
}

// CrÃ©ation du contexte avec une valeur par dÃ©faut
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personnalisÃ© pour utiliser le contexte d'authentification
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit Ãªtre utilisÃ© Ã  l\'intÃ©rieur d\'un AuthProvider');
  }
  return context;
};

// Props pour le provider d'authentification
interface AuthProviderProps {
  children: ReactNode;
}

// Provider d'authentification
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Ã‰tat d'authentification
  const [state, setState] = useState<AuthState>({
    supabaseUser: null,
    userProfile: null,
    agencyName: null,
    agencyAddress: null,
    session: null,
    loading: true,
    error: null,
    shouldShowChangelog: false,
  });
  
  // Fonction pour mettre Ã  jour l'Ã©tat
  const updateState = (newState: Partial<AuthState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  // Fonction pour effacer les erreurs
  const clearError = () => {
    updateState({ error: null });
  };

  // Fonction pour vérifier le statut du changelog
  const checkChangelogStatus = async () => {
    try {
      const shouldShow = await ChangelogService.shouldShowChangelog();
      updateState({ shouldShowChangelog: shouldShow });
    } catch (error) {
      logger.error('Erreur lors de la vérification du changelog:', error);
      updateState({ shouldShowChangelog: false });
    }
  };

  // Fonction pour marquer le changelog comme vu
  const markChangelogAsSeen = async () => {
    try {
      updateState({ shouldShowChangelog: false }); // Fermer immédiatement le popup
      await ChangelogService.updateLastSeenVersion('1.0.1'); // Version actuelle
    } catch (error) {
      logger.error('Erreur lors de la mise à jour du changelog:', error);
    }
  };

  // Fonction d'inscription
  const signUp = async (data: SignUpData) => {
    try {
      updateState({ loading: true, error: null });
      const { error } = await authService.signUp(data);
      
      if (error) {
        throw error;
      }
      
      // L'inscription nÃ©cessite une vÃ©rification par email, donc on ne met pas Ã  jour l'utilisateur ici
      updateState({ loading: false });
    } catch (error) {
      logger.error('Erreur lors de l\'inscription:', error);
      updateState({ loading: false, error: error as Error });
    }
  };

  // Fonction de connexion
  const signIn = async (email: string, password: string) => {
    try {
      updateState({ loading: true, error: null });
      const { user: supabaseUser, session, error } = await authService.signIn(email, password);
      
      if (error) {
        throw error;
      }
      
      if (!session) {
        throw new Error('Aucune session crÃ©Ã©e lors de la connexion');
      }
      
      logger.info('Connexion rÃ©ussie, rÃ©cupÃ©ration du profil utilisateur');
      
      // RÃ©cupÃ©rer le profil utilisateur complet
      if (supabaseUser) {
        const { userProfile, error: profileError } = await authService.getUserProfile(supabaseUser.id);
        
        console.log('🔍 AuthContext: userProfile récupéré:', userProfile);
        
        if (profileError) {
          logger.error('Erreur lors de la rÃ©cupÃ©ration du profil utilisateur:', profileError);
          // On continue malgrÃ¨ l'erreur, l'utilisateur pourra toujours utiliser l'application
        }
        
        // Si un profil utilisateur est trouvÃ et qu'il a une agence, rÃ©cupÃ©rer les informations de l'agence
        let agencyName = null;
        let agencyAddress = null;
        if (userProfile && userProfile.agency_id) {
          const { agencyName: fetchedAgencyName, agencyAddress: fetchedAgencyAddress, error: agencyError } = await authService.getAgencyName(userProfile.agency_id);
          
          // Ajouter des logs pour dÃ©boguer
          console.log('Informations de l\'agence rÃ©cupÃ©rÃ©es:', { fetchedAgencyName, fetchedAgencyAddress });
          
          if (agencyError) {
            logger.error('Erreur lors de la rÃ©cupÃ©ration des informations de l\'agence:', agencyError);
          } else {
            agencyName = fetchedAgencyName;
            agencyAddress = fetchedAgencyAddress;
            // VÃ©rifier les valeurs assignÃ©es
            console.log('Valeurs assignÃ©es:', { agencyName, agencyAddress });
            
            // VÃ©rifier s'il y a un marqueur agence en attente (aprÃ¨s inscription)
            const pendingMarker = sessionStorage.getItem('pendingAgencyMarker');
            if (pendingMarker && userProfile.is_owner) {
              try {
                const markerData = JSON.parse(pendingMarker);
                logger.info('CrÃ©ation du marqueur agence avec coordonnÃ©es prÃ©-gÃ©ocodÃ©es');
                
                // CrÃ©er le marqueur agence avec les coordonnÃ©es dÃ©jÃ  gÃ©ocodÃ©es
                agencyMarkerService.createAgencyMarkerIfNotExists(
                  userProfile.agency_id,
                  supabaseUser.id,
                  markerData.address,
                  markerData.agencyName
                ).then(() => {
                  sessionStorage.removeItem('pendingAgencyMarker');
                  logger.info('Marqueur agence crÃ©Ã© avec succÃ¨s');
                }).catch((error) => {
                  logger.error('Erreur lors de la crÃ©ation du marqueur agence:', error);
                });
              } catch (error) {
                logger.error('Erreur lors du parsing du marqueur en attente:', error);
                sessionStorage.removeItem('pendingAgencyMarker');
              }
            }
            
            // DÉSACTIVÉ: Marqueurs d'agence (fonctionnalité retirée)
            // La création automatique de marqueurs d'agence a été désactivée
            // Le code est conservé pour référence mais n'est plus exécuté
            /*
            if (userProfile.is_owner && fetchedAgencyAddress) {
              agencyMarkerService.checkAgencyMarkerExists(userProfile.agency_id)
                .then(({ exists, hasCoordinates }) => {
                  if (!exists) {
                    logger.info('CrÃ©ation du marqueur agence manquant');
                    return agencyMarkerService.createAgencyMarkerIfNotExists(
                      userProfile.agency_id,
                      supabaseUser.id,
                      fetchedAgencyAddress,
                      fetchedAgencyName || 'Agence'
                    );
                  } else if (!hasCoordinates) {
                    logger.info('Mise Ã  jour des coordonnÃ©es du marqueur agence');
                    return agencyMarkerService.geocodeAndUpdateAgencyMarker(
                      userProfile.agency_id,
                      fetchedAgencyAddress
                    );
                  }
                  return Promise.resolve({ success: true });
                })
                .then((result) => {
                  if (result && result.success) {
                    logger.info('Marqueur agence vÃ©rifiÃ© et mis Ã  jour avec succÃ¨s');
                  } else if (result && 'error' in result) {
                    logger.warn('Erreur lors de la mise Ã  jour du marqueur agence:', result.error);
                  }
                })
                .catch((error) => {
                  logger.error('Exception lors de la vÃ©rification du marqueur agence:', error);
                });
            }
            */
          }
        }
        
        updateState({ 
          supabaseUser, 
          userProfile, 
          agencyName,
          agencyAddress,
          session, 
          loading: false 
        });

        // Vérifier le statut du changelog après connexion
        await checkChangelogStatus();
      } else {
        updateState({ 
          supabaseUser, 
          session, 
          loading: false 
        });
      }
    } catch (error) {
      logger.error('Erreur lors de la connexion:', error);
      updateState({ loading: false, error: error as Error });
    }
  };

  // Fonction de dÃ©connexion
  const signOut = async () => {
    try {
      updateState({ loading: true, error: null });
      const { error } = await authService.signOut();
      
      if (error) {
        throw error;
      }
      
      updateState({ 
        supabaseUser: null, 
        userProfile: null, 
        agencyName: null, 
        agencyAddress: null,
        session: null, 
        loading: false 
      });
      
      // Rediriger vers la page de connexion avec l'URL complÃ¨te
      window.location.href = window.location.origin + '/login';
    } catch (error) {
      logger.error('Erreur lors de la dÃ©connexion:', error);
      updateState({ loading: false, error: error as Error });
    }
  };

  // Fonction de rÃ©initialisation de mot de passe
  const resetPassword = async (email: string) => {
    try {
      updateState({ loading: true, error: null });
      const { error } = await authService.resetPassword(email);
      
      if (error) {
        throw error;
      }
      
      updateState({ loading: false });
    } catch (error) {
      logger.error('Erreur lors de la rÃ©initialisation du mot de passe:', error);
      updateState({ loading: false, error: error as Error });
    }
  };

  // Fonction de mise Ã  jour du mot de passe
  const updatePassword = async (newPassword: string) => {
    try {
      updateState({ loading: true, error: null });
      const { error } = await authService.updatePassword(newPassword);
      
      if (error) {
        throw error;
      }
      
      updateState({ loading: false });
    } catch (error) {
      logger.error('Erreur lors de la mise Ã  jour du mot de passe:', error);
      updateState({ loading: false, error: error as Error });
    }
  };
  
  // Fonction de mise Ã  jour du profil utilisateur
  const updateUserProfile = async (userData: UserUpdateInput) => {
    try {
      if (!state.supabaseUser) {
        throw new Error('Utilisateur non connectÃ©');
      }
      
      updateState({ loading: true, error: null });
      
      const { userProfile, error: profileError } = await authService.updateUserProfile(
        state.supabaseUser.id,
        userData
      );
      
      if (profileError) {
        throw profileError;
      }
      
      // Si l'agence a changÃ©, rÃ©cupÃ©rer le nouveau nom de l'agence
      let agencyName = state.agencyName;
      if (userProfile && userData.agency_id && userData.agency_id !== state.userProfile?.agency_id) {
        const { agencyName: fetchedAgencyName, error: agencyError } = await authService.getAgencyName(userData.agency_id);
        
        if (agencyError) {
          logger.error('Erreur lors de la rÃ©cupÃ©ration du nom de l\'agence:', agencyError);
        } else {
          agencyName = fetchedAgencyName;
        }
      }
      
      updateState({ 
        userProfile, 
        agencyName,
        loading: false 
      });
    } catch (error) {
      logger.error('Erreur lors de la mise Ã  jour du profil utilisateur:', error);
      updateState({ loading: false, error: error as Error });
    }
  };

  // Effet pour rÃ©cupÃ©rer la session au chargement et configurer l'Ã©couteur
  useEffect(() => {
    const initAuth = async () => {
      try {
        updateState({ loading: true });
        // RÃ©cupÃ©rer la session actuelle
        const { session, error: sessionError } = await authService.getSession();
        
        if (sessionError) {
          throw sessionError;
        }
        
        // Si une session existe, rÃ©cupÃ©rer l'utilisateur
        if (session) {
          const { user: supabaseUser, error: userError } = await authService.getCurrentUser();
          
          if (userError) {
            throw userError;
          }
          
          logger.info('Session existante trouvÃ©e au chargement');
          
          // RÃ©cupÃ©rer le profil utilisateur complet
          if (supabaseUser) {
            const { userProfile, error: profileError } = await authService.getUserProfile(supabaseUser.id);
            
            if (profileError) {
              logger.error('Erreur lors de la rÃ©cupÃ©ration du profil utilisateur:', profileError);
              // On continue malgrÃ© l'erreur, l'utilisateur pourra toujours utiliser l'application
            }
            
            // Si un profil utilisateur est trouvÃ© et qu'il a une agence, rÃ©cupÃ©rer le nom de l'agence
            let agencyName = null;
            if (userProfile && userProfile.agency_id) {
              const { agencyName: fetchedAgencyName, error: agencyError } = await authService.getAgencyName(userProfile.agency_id);
              
              if (agencyError) {
                logger.error('Erreur lors de la rÃ©cupÃ©ration du nom de l\'agence:', agencyError);
              } else {
                agencyName = fetchedAgencyName;
              }
            }
            
            updateState({ 
              supabaseUser, 
              userProfile, 
              agencyName,
              session, 
              loading: false 
            });

            // Vérifier le statut du changelog au chargement
            await checkChangelogStatus();
          } else {
            updateState({ 
              supabaseUser, 
              session, 
              loading: false 
            });
          }
        } else {
          updateState({ loading: false });
        }
      } catch (error) {
        logger.error('Erreur lors de l\'initialisation de l\'authentification:', error);
        updateState({ loading: false, error: error as Error });
      }
    };

    // Initialiser l'authentification
    initAuth();

    // Configurer l'Ã©couteur pour les changements d'Ã©tat d'authentification
    const unsubscribe = authService.onAuthStateChange((event, session) => {
      logger.debug(`Ã‰vÃ©nement d'authentification: ${event}`, { 
        event, 
        sessionExists: !!session,
        userId: session?.user?.id
      });

      // Mettre Ã  jour l'Ã©tat en fonction de l'Ã©vÃ©nement
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Mettre Ã  jour l'Ã©tat avec les nouvelles donnÃ©es de session
        const updateUserData = async () => {
          try {
            const { user: supabaseUser } = await authService.getCurrentUser();
            
            if (supabaseUser) {
              // RÃ©cupÃ©rer le profil utilisateur complet
              const { userProfile } = await authService.getUserProfile(supabaseUser.id);
              
              // RÃ©cupÃ©rer les informations de l'agence si le profil existe
              let agencyName = null;
              let agencyAddress = null;
              if (userProfile && userProfile.agency_id) {
                const { agencyName: name, agencyAddress: address } = await authService.getAgencyName(userProfile.agency_id);
                agencyName = name;
                agencyAddress = address;
              }
              
              // Mettre Ã  jour l'Ã©tat avec toutes les donnÃ©es
              updateState({
                supabaseUser,
                userProfile,
                agencyName,
                agencyAddress,
                session,
                loading: false,
                error: null
              });

              // Vérifier le statut du changelog après reconnexion
              await checkChangelogStatus();
            }
          } catch (error) {
            logger.error('Erreur lors de la rÃ©cupÃ©ration des donnÃ©es utilisateur:', error);
            updateState({
              error: error as Error,
              loading: false
            });
          }
        };
        
        updateUserData();
      } else if (event === 'SIGNED_OUT') {
        // RÃ©initialiser l'Ã©tat lorsque l'utilisateur se dÃ©connecte
        updateState({
          supabaseUser: null,
          userProfile: null,
          agencyName: null,
          agencyAddress: null,
          session: null,
          loading: false,
          error: null
        });
      } else if (event === 'USER_UPDATED' && session) {
        // Mettre Ã  jour l'utilisateur
        const updateUser = async () => {
          try {
            const { user: supabaseUser } = await authService.getCurrentUser();
            
            if (supabaseUser && state.userProfile) {
              updateState({ 
                supabaseUser, 
                session 
              });
            }
          } catch (error) {
            logger.error('Erreur lors de la mise Ã  jour des donnÃ©es utilisateur:', error);
          }
        };
        
        updateUser();
      }
    });

    // Nettoyer l'Ã©couteur lors du dÃ©montage
    return () => {
      unsubscribe();
    };
  }, []);

  // Valeur du contexte
  const contextValue: AuthContextType = {
    ...state,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateUserProfile,
    clearError,
    checkChangelogStatus,
    markChangelogAsSeen,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};



